/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { TripPreferenceType } from '../../types/tripPreferences';
import { useAppDispatch, useAppSelector } from '../../redux/redux-helpers';
import {
    applyTripPreferences,
    disableTripPreferences,
    updateTripPreferences,
    getTripPreferences,
} from '../../actions/tripPreferences';

interface TripPreferenceProviderProps {
    children: React.ReactNode;
}

export type TripPreferenceChanges = Partial<TripPreferenceType>;

/**
 * Defines the core preference-related actions available to components
 */
interface PreferenceActions {
    /** Applies current preferences to the trip search */
    onApplyPreferences: () => Promise<void>;
    /** Disables all trip preferences */
    onDisablePreferences: () => void;
    /** Updates the selected preferences */
    onChangePreferences: (preferences: string[]) => void;
    /** Controls the visibility of the preferences modal */
    onPreferenceModal: (isOpen: boolean) => void;
    /** Controls whether to show management screen after login */
    onOpenManagementAfterLogin: (show: boolean) => void;
}

/**
 * Defines the state shape for trip preferences
 */
interface PreferenceState {
    /** Array of selected preference identifiers */
    preferences: string[];
    /** Indicates if preferences have been loaded */
    loaded: boolean;
    /** Indicates if preferences are currently enabled */
    isEnabled: boolean;
    /** Current state of the preferences modal */
    isOpenPreferencesModal: boolean;
    /** Flag to track if management should be shown post-login */
    openManagementAfterLogin: boolean;
    /** Indicates if preferences are currently being loaded */
    loadingPreferences: boolean;
    /** Flag to apply preferences after an update */
    applyPreferencesAfterUpdate: boolean;
}

/**
 * Combined context type that includes both actions and state
 */
type ContextValue = PreferenceActions & PreferenceState;

const TripPreferenceContext = React.createContext<ContextValue | undefined>(undefined);

/**
 * Provider component that manages trip preferences state and operations.
 * Handles preference updates, application, and modal interactions while
 * maintaining synchronization with user authentication.
 */
export function TripPreferenceProvider({ children }: TripPreferenceProviderProps) {
    // Local states
    const [isOpenPreferencesModal, setIsOpenPreferencesModal] = React.useState(false);
    const [openManagementAfterLogin, setOpenManagementAfterLogin] = React.useState(false);
    const [applyPreferencesAfterUpdate, setApplyPreferencesAfterUpdate] =
        React.useState(false);
    const [loadingPreferences, setLoadingPreferences] = React.useState(false);
    // Redux states
    const tripPreferences = useAppSelector((state) => state?.tripPreferences ?? {});
    const search = useAppSelector((state) => state?.search ?? {});
    const account = useAppSelector((state) => state.auth.account);

    const dispatch = useAppDispatch();
    const accountId = account?.account?.account_id;

    // Load user's trip preferences when they log in
    React.useEffect(() => {
        if (accountId) {
            dispatch(getTripPreferences(account));
        }
    }, [accountId]);

    // Clear preferences when user is not authenticated
    React.useEffect(() => {
        if (!accountId) {
            dispatch(disableTripPreferences());
        }
    }, [dispatch]);

    const onPreferenceModal = React.useCallback((isOpen: boolean) => {
        setIsOpenPreferencesModal(isOpen);
    }, []);

    const onDisablePreferences = React.useCallback(() => {
        dispatch(disableTripPreferences());
    }, [dispatch]);

    const onChangePreferences = React.useCallback(
        (preferences: string[]) => {
            dispatch(updateTripPreferences(preferences));
        },
        [dispatch]
    );

    const contextValue: ContextValue = React.useMemo(() => {
        const onApplyPreferences = async () => {
            const preferences = tripPreferences?.preferences ?? [];
            try {
                // Open modal if user is not logged in or has no preferences
                if (!account || preferences?.length === 0) {
                    setApplyPreferencesAfterUpdate(true);
                    onPreferenceModal(true);
                    return;
                }
                // Apply preferences and handle loading state
                setApplyPreferencesAfterUpdate(false);
                setLoadingPreferences(true);
                await dispatch(applyTripPreferences());
            } finally {
                setLoadingPreferences(false);
            }
        };

        return {
            onApplyPreferences,
            onDisablePreferences,
            onChangePreferences,
            onPreferenceModal,
            openManagementAfterLogin,
            onOpenManagementAfterLogin: setOpenManagementAfterLogin,
            preferences: tripPreferences?.preferences ?? [],
            loaded: tripPreferences?.loaded ?? false,
            isOpenPreferencesModal,
            loadingPreferences,
            isEnabled: search?.tripPreferences ?? false,
            applyPreferencesAfterUpdate,
        };
    }, [
        isOpenPreferencesModal,
        openManagementAfterLogin,
        loadingPreferences,
        tripPreferences,
        search?.tripPreferences,
        account,
        applyPreferencesAfterUpdate,
    ]);

    return (
        <TripPreferenceContext.Provider value={contextValue}>
            {children}
        </TripPreferenceContext.Provider>
    );
}

/**
 * Hook to access trip preferences context.
 * Must be used within a TripPreferenceProvider component.
 * @throws Error if used outside of TripPreferenceProvider
 */
export function useTripPreferences() {
    const context = React.useContext(TripPreferenceContext);

    if (context === undefined) {
        throw new Error('useTripPreferenceContext must be within TripPreferenceProvider');
    }

    return context;
}
